import { SyntheticEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import styled from 'styled-components';

// The portal event guards, backdrop, dismissal and viewport placement shared by the cell context menu and the verses
// picker (issue #4169), and the upward opening of the planner's row menu and its Repeats flyout (issue #4170).

// how close to the window's edges a menu may come
export const viewportMargin = 8;

/** The position that keeps a box of `size` starting at `position` inside a viewport of `viewportSize` */
export function clampToViewport(position: number, size: number, viewportSize: number, margin: number): number {
  return Math.max(margin, Math.min(position, viewportSize - margin - size));
}

/**
 * The start of a box of `size` opened at `position`, as a native menu opens at the pointer: after the position when it
 * fits there, otherwise before it (ending at the position), otherwise clamped inside the viewport
 */
export function placeFromPoint(position: number, size: number, viewportSize: number, margin: number): number {
  if (position + size <= viewportSize - margin) {
    return position;
  }
  if (position - size >= margin) {
    return position - size;
  }
  return clampToViewport(position, size, viewportSize, margin);
}

/**
 * Whether a menu that hangs below its anchor opens upward instead, ending at `upwardEnd` (the viewport y its bottom edge
 * moves to): when its `bottom` below the anchor would pass the bottom margin of the visible area (`clipTop` to
 * `clipBottom`: the window, or the part of it the scroll container holding the menu shows) and its `height` fits
 * between the top margin and `upwardEnd`. A menu that fits on neither side stays below, where the page can scroll to it.
 */
export function opensUpward({
  bottom,
  height,
  upwardEnd,
  clipTop,
  clipBottom,
  margin,
}: {
  bottom: number;
  height: number;
  upwardEnd: number;
  clipTop: number;
  clipBottom: number;
  margin: number;
}): boolean {
  return bottom > clipBottom - margin && upwardEnd - height >= clipTop + margin;
}

/**
 * Where a menu positioned absolutely against its parent element, its anchor, hangs: below it ('below': the row menu
 * under its type icon), ending at the anchor's top when it opens upward; or beside it from its top down ('beside': the
 * Repeats flyout beside its line), ending at the anchor's bottom when it opens upward
 */
export type MenuPlacement = 'below' | 'beside';

/** The style that makes a menu hanging from its anchor at `placement` open upward instead; the anchor is positioned */
export function opensUpwardCss(placement: MenuPlacement): string {
  return `
    top: auto;
    bottom: ${placement === 'below' ? '100%' : '0'};
  `;
}

/**
 * A ref for a menu hanging from its anchor at `placement`, and whether it opens upward instead because it would pass
 * the bottom of the visible area: the window, or the scroll container clipping it when that ends higher (the planner's
 * grid ends above a banner shown under it). Measured when the menu appears and again while it is open whenever
 * anything scrolls or the window is resized, always from where it hangs by default (the anchor's place and the menu's
 * height, never the menu's current place), so an upward menu does not flip back and forth. Measured again, once React
 * has committed it, whenever `remeasureKey` changes: a flyout passes its parent menu's orientation, since the parent
 * flipping moves the flyout's anchor after the flyout measured on the same scroll or resize.
 */
export function useOpensUpward(placement: MenuPlacement, remeasureKey?: unknown) {
  const [isUpward, setIsUpward] = useState(false);
  // the open menu's measurement, for the remeasure below
  const measureRef = useRef<() => void>(undefined);
  const ref = useCallback(
    (menu: HTMLElement | null) => {
      const anchor = menu?.parentElement;
      if (!menu || !anchor) {
        return;
      }
      const scrollContainer = getScrollContainer(anchor);
      const measure = () => {
        const { height } = menu.getBoundingClientRect();
        const { top, bottom } = anchor.getBoundingClientRect();
        const clip = scrollContainer?.getBoundingClientRect();
        setIsUpward(
          opensUpward({
            bottom: (placement === 'below' ? bottom : top) + height,
            height,
            upwardEnd: placement === 'below' ? top : bottom,
            clipTop: Math.max(0, clip?.top ?? 0),
            clipBottom: Math.min(window.innerHeight, clip?.bottom ?? Infinity),
            margin: viewportMargin,
          }),
        );
      };
      measure();
      measureRef.current = measure;
      const controller = new AbortController();
      onViewportChange(measure, controller.signal);
      return () => {
        controller.abort();
        measureRef.current = undefined;
        // the next time it appears, it hangs where it does by default until measured
        setIsUpward(false);
      };
    },
    [placement],
  );
  useLayoutEffect(() => {
    measureRef.current?.();
  }, [remeasureKey]);
  return { ref, isUpward };
}

/** Calls handler whenever anything on the page scrolls or the window is resized, until signal aborts */
export function onViewportChange(handler: () => void, signal: AbortSignal) {
  window.addEventListener('scroll', handler, { capture: true, signal });
  window.addEventListener('resize', handler, { signal });
}

// the nearest ancestor that scrolls vertically, which clips what it holds
function getScrollContainer(element: HTMLElement): HTMLElement | undefined {
  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (['auto', 'scroll'].includes(getComputedStyle(ancestor).overflowY)) {
      return ancestor;
    }
  }
  return undefined;
}

function stopPropagation(event: SyntheticEvent) {
  event.stopPropagation();
}

// React events bubble through a portal to its React ancestors: without these, a key pressed or a click made in the
// menu, the verses picker or their backdrop would reach the cell (a letter opens its editor, Backspace clears it) and
// the planner grid (arrow keys move the selection)
export const portalEventGuards = {
  onKeyDown: stopPropagation,
  onMouseDown: stopPropagation,
  onPointerDown: stopPropagation,
  onClick: stopPropagation,
  onContextMenu: (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  },
};

/** Calls onDismiss on Escape; with isClosedByScroll, also when the page scrolls or the window is resized */
export function useDismiss(onDismiss: () => void, { isClosedByScroll }: { isClosedByScroll?: boolean } = {}) {
  // the latest callback, without re-registering the listeners each render
  const onDismissRef = useRef(onDismiss);
  useLayoutEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    const controller = new AbortController();
    const dismiss = () => onDismissRef.current();
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown, { signal: controller.signal });
    if (isClosedByScroll) {
      onViewportChange(dismiss, controller.signal);
    }
    return () => controller.abort();
  }, [isClosedByScroll]);
}

// the menu and the verses picker float over the planner; their backdrop lies just under them
export const popupZIndex = 20;

/**
 * A transparent layer over the whole page, just under an open menu or verses picker, that closes it when clicked. It
 * is what makes the press that closes the popup do nothing else: every event of that press lands on the backdrop, not
 * on a cell or control underneath, so on a phone the tap that closes cell A's menu does not open cell B's editor, and
 * on a computer a click on another cell only closes the menu. It closes on click, the last event of a press, so it
 * stays under the finger for the whole press, including the mousedown iOS Safari sends after touchend. It cancels
 * Android's own long-press menu. (A control under it, such as the search box, takes a second click once the popup has
 * closed.)
 *
 * The planner grid is its own scroll container, so a wheel over the backdrop scrolls nothing. With isPassingThrough
 * (the menu, which closes as a native menu does) a wheel closes the popup, and the next one scrolls the grid; a
 * right-click closes it and goes on to whatever lies under the pointer, so a right-click on another cell opens that
 * cell's menu in one gesture. Without it (the verses picker, which is modal) a wheel over the backdrop does nothing,
 * and a right-click only cancels the popup. A finger dragged over either backdrop does nothing.
 */
export const PopupBackdrop = ({
  onDismiss,
  isPassingThrough,
}: {
  onDismiss: () => void;
  isPassingThrough?: boolean;
}) => (
  <Backdrop
    data-testid="popup-backdrop"
    data-popup-backdrop={true}
    aria-hidden={true}
    {...portalEventGuards}
    onMouseDown={(event) => {
      // the focus stays in the popup until the press ends
      event.preventDefault();
      event.stopPropagation();
    }}
    onClick={(event) => {
      event.stopPropagation();
      onDismiss();
    }}
    onWheel={isPassingThrough ? onDismiss : undefined}
    onContextMenu={(event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isPassingThrough) {
        onDismiss();
        return;
      }
      // closed at once, so the backdrop is out of the page before the pointer's target is looked up
      // eslint-disable-next-line @eslint-react/dom-no-flush-sync -- once per right-click, and the lookup needs it
      flushSync(onDismiss);
      const { clientX, clientY, screenX, screenY } = event;
      const target = document.elementFromPoint(clientX, clientY);
      // another popup's backdrop would take the right-click again: it stops here
      if (!target || target.closest('[data-popup-backdrop]')) {
        return;
      }
      target.dispatchEvent(
        new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          button: 2,
          clientX,
          clientY,
          screenX,
          screenY,
        }),
      );
    }}
  />
);

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  touch-action: none;
  z-index: ${popupZIndex - 1};
`;
